import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ExternalDataService, PlatformChargeTransaction, Agent, Zone, BulkRemittanceUpdateRequest } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';

@Component({
  selector: 'app-platform-charge-transactions',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './platform-charge-transactions.component.html',
  styleUrl: './platform-charge-transactions.component.css'
})
export class PlatformChargeTransactionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Data properties
  transactions: PlatformChargeTransaction[] = [];
  agents: Agent[] = [];
  zones: Zone[] = [];
  totalAmount = 0;
  
  // Loading and error states
  loading = false;
  errorMessage = '';
  successMessage = '';
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;
  
  // Filter properties
  selectedAgentId = '';
  selectedZoneId = '';
  selectedHasRemittance: string = '';
  fromDate = '';
  toDate = '';
  
  // Bulk remittance properties
  selectedTransactions: string[] = [];
  showBulkRemittanceForm = false;
  bulkRemittanceForm = {
    remittanceDate: '',
    remittanceMedium: '',
    remittanceNotes: ''
  };
  bulkRemittanceLoading = false;
  
  // Make Math available in template
  Math = Math;

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadAgents();
    this.loadZones();
    this.loadTransactions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgents(): void {
    this.externalDataService.getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        }
      });
  }

  loadZones(): void {
    this.externalDataService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
        }
      });
  }

  loadTransactions(): void {
    this.loading = true;
    this.errorMessage = '';
    
    const pageIndex = this.currentPage - 1;
    const agentId = this.selectedAgentId || undefined;
    const geoZoneId = this.selectedZoneId || undefined;
    const hasRemittance = this.selectedHasRemittance === 'true' ? true : 
                          this.selectedHasRemittance === 'false' ? false : 
                          undefined;
    const fromDate = this.fromDate || undefined;
    const toDate = this.toDate || undefined;
    
    this.externalDataService.getPlatformChargeTransactions(
      pageIndex, 
      this.pageSize, 
      agentId, 
      geoZoneId, 
      hasRemittance,
      fromDate,
      toDate
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.transactions = data.items;
          this.totalCount = data.totalCount;
          this.totalAmount = data.totalAmount;
          this.calculateTotalPages();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading platform charge transactions:', error);
          
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view platform charge transactions.';
          } else if (error.status === 404) {
            this.errorMessage = 'Platform charge transactions not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to load platform charge transactions. Please try again.';
          }
          
          this.loading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTransactions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1; // Reset to first page when changing page size
    this.loadTransactions();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.selectedAgentId = '';
    this.selectedZoneId = '';
    this.selectedHasRemittance = '';
    this.fromDate = '';
    this.toDate = '';
    this.currentPage = 1;
    this.loadTransactions();
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  formatPercentage(percentage: number): string {
    return `${percentage}%`;
  }

  // Totals for current page items
  get totalPlatformChargeAmount(): number {
    return this.transactions.reduce((sum, t) => sum + (t.platformChargeAmount || 0), 0);
  }

  get totalCgst(): number {
    return this.transactions.reduce((sum, t) => sum + (t.cgst || 0), 0);
  }

  get totalSgst(): number {
    return this.transactions.reduce((sum, t) => sum + (t.sgst || 0), 0);
  }

  // Bulk remittance methods
  toggleTransactionSelection(transactionId: string): void {
    const index = this.selectedTransactions.indexOf(transactionId);
    if (index > -1) {
      this.selectedTransactions.splice(index, 1);
    } else {
      this.selectedTransactions.push(transactionId);
    }
  }

  isTransactionSelected(transactionId: string): boolean {
    return this.selectedTransactions.includes(transactionId);
  }

  selectAllTransactions(): void {
    this.selectedTransactions = this.transactions.map(t => t.id);
  }

  clearSelection(): void {
    this.selectedTransactions = [];
    this.showBulkRemittanceForm = false;
    this.resetBulkRemittanceForm();
  }

  openBulkRemittanceForm(): void {
    if (this.selectedTransactions.length === 0) {
      this.errorMessage = 'Please select at least one transaction for bulk remittance update.';
      return;
    }
    
    // Set default date to today
    this.bulkRemittanceForm.remittanceDate = new Date().toISOString().split('T')[0];
    this.showBulkRemittanceForm = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeBulkRemittanceForm(): void {
    this.showBulkRemittanceForm = false;
    this.resetBulkRemittanceForm();
  }

  resetBulkRemittanceForm(): void {
    this.bulkRemittanceForm = {
      remittanceDate: '',
      remittanceMedium: '',
      remittanceNotes: ''
    };
  }

  submitBulkRemittance(): void {
    if (!this.validateBulkRemittanceForm()) {
      return;
    }

    this.bulkRemittanceLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request: BulkRemittanceUpdateRequest = {
      platformChargeTransactionIds: this.selectedTransactions,
      remittanceDate: new Date(this.bulkRemittanceForm.remittanceDate).toISOString(),
      remittanceMedium: this.bulkRemittanceForm.remittanceMedium,
      remittanceNotes: this.bulkRemittanceForm.remittanceNotes
    };

    this.externalDataService.updateBulkRemittance(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = `Successfully updated remittance for ${this.selectedTransactions.length} transaction(s).`;
          this.bulkRemittanceLoading = false;
          this.closeBulkRemittanceForm();
          this.clearSelection();
          this.loadTransactions(); // Refresh the data
        },
        error: (error) => {
          console.error('Error updating bulk remittance:', error);
          
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to update remittance.';
          } else if (error.status === 404) {
            this.errorMessage = 'One or more transactions not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to update remittance. Please try again.';
          }
          
          this.bulkRemittanceLoading = false;
        }
      });
  }

  private validateBulkRemittanceForm(): boolean {
    if (!this.bulkRemittanceForm.remittanceDate) {
      this.errorMessage = 'Remittance date is required.';
      return false;
    }

    if (!this.bulkRemittanceForm.remittanceMedium?.trim()) {
      this.errorMessage = 'Remittance medium is required.';
      return false;
    }

    if (!this.bulkRemittanceForm.remittanceNotes?.trim()) {
      this.errorMessage = 'Remittance notes are required.';
      return false;
    }

    return true;
  }

  get selectedTransactionsCount(): number {
    return this.selectedTransactions.length;
  }

  get selectedTransactionsTotalAmount(): number {
    return this.transactions
      .filter(t => this.selectedTransactions.includes(t.id))
      .reduce((sum, t) => sum + (t.platformChargeAmount || 0), 0);
  }

  // Export functionality
  exportToCSV(): void {
    if (this.transactions.length === 0) {
      this.errorMessage = 'No data to export.';
      return;
    }

    // Define CSV headers
    const headers = [
      'Agent Name',
      'Platform Charge %',
      'Platform Charge Amount',
      'CGST',
      'SGST',
      'Collection Date',
      'Remittance Date',
      'Remittance Medium',
      'Remittance Notes'
    ];

    // Convert transactions to CSV rows
    const rows = this.transactions.map(transaction => [
      this.escapeCSVValue(transaction.agentName),
      transaction.platformChargeInPercentage.toString(),
      transaction.platformChargeAmount.toString(),
      transaction.cgst.toString(),
      transaction.sgst.toString(),
      this.formatDateForExport(transaction.collectionDate),
      this.formatDateForExport(transaction.remittanceDate),
      this.escapeCSVValue(transaction.remittanceMedium || 'N/A'),
      this.escapeCSVValue(transaction.remittanceNotes || 'N/A')
    ]);

    // Add totals row
    rows.push([
      'Total',
      '',
      this.totalPlatformChargeAmount.toString(),
      this.totalCgst.toString(),
      this.totalSgst.toString(),
      '',
      '',
      '',
      ''
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `platform-charge-transactions-${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    this.successMessage = `Data exported successfully to ${filename}`;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  private escapeCSVValue(value: string | null | undefined): string {
    if (!value) return '';
    
    // Convert to string and escape
    const stringValue = String(value);
    
    // If value contains comma, newline, or quote, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  }

  private formatDateForExport(dateString: string | null): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  }
}

